<template>
  <ContextColorProvider context="brand">
    <NavBar data-testid="navbar">
      <template #left>
        <BackButton class="position-detail__back-button" @click="back" />
      </template>
      <template #center>Posição</template>
      <div />
    </NavBar>
    <div class="position-detail__content">
      <template v-if="isLoading">
        <LoaderIndicator with-container />
      </template>
      <template v-else-if="hasError">
        <div class="position__helper-view">
          <ActionableHelperView
            class="position__error"
            :on-retry="loadPosition"
          />
        </div>
      </template>
      <template v-else-if="!position">
        <Heading :level="2" class="product-without-quotas_title">
          Detalhes
        </Heading>
        <hr class="product-without-quotas_divider" />
        <BaseText class="product-without-quotas_text" size="xl">
          Você não possui cotas compradas desse produto.
        </BaseText>
      </template>
      <template v-else>
        <Heading :level="2" data-testid="heading">Detalhes</Heading>
        <Row :has-line="false">
          <template #top-left>
            <span class="text-p-3">{{ position.businessName }}</span>
          </template>
          <template #bottom-left>
            <span class="text-p-5 position__subtitle">
              Emissor: {{ position.investments[0].emitterName }}
            </span>
          </template>
        </Row>
        <div class="position__secondary-subtitle">
          <p v-if="position.investments[0].issuanceDate" class="text-p-5">
            Data de emissão: {{ position.investments[0].issuanceDate }}
          </p>
          <p v-if="position.investments[0].expiresIn" class="text-p-5">
            Data de vencimento: {{ position.investments[0].expiresIn }}
          </p>
          <p v-if="showContractedRate" class="text-p-5">
            Taxa contratada inicialmente: {{ formatFixedTax }}
          </p>
        </div>
        <AccordionWrapper data-testid="accordion-wrapper">
          <Accordion
            v-for="(investment, index) in position.investments"
            :key="`${position.businessName}${index}`"
            :title="`Cotas: ${investment.quantity}`"
            :is-primary="true"
          >
            <div
              class="position-investment__content"
              data-testid="detailed-rows"
            >
              <div class="position-investment__details">
                <Row>
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.initialAmountApplied | money(2) }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Valor aplicado
                    </p>
                  </template>
                </Row>
                <Row>
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.convertedAt }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Data da aplicação
                    </p>
                  </template>
                </Row>
                <Row>
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.quantity }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Qtd. atual de cotas
                    </p>
                  </template>
                </Row>
                <Row>
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.initialQuote | money(8) }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Valor da cota na compra
                    </p>
                  </template>
                </Row>
                <Row>
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.todayQuote | money(8) }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Último valor atualizado da cota
                    </p>
                  </template>
                </Row>
              </div>
              <div class="position-investment__details">
                <Row :has-line="false">
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.grossValue | money }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Valor bruto
                    </p>
                  </template>
                </Row>
                <Row
                  v-if="!hideDetailsCostsAndNetBalance(investment.subtype)"
                  :has-line="false"
                >
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.incomeTax | money }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      (-) IR
                    </p>
                  </template>
                </Row>
                <Row
                  v-if="!hideDetailsCostsAndNetBalance(investment.subtype)"
                  :has-line="false"
                >
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.transactionTax | money }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      (-) IOF
                    </p>
                  </template>
                </Row>
                <Row v-if="investment.liquidationPendingValue">
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.liquidationPendingValue }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Valor a liquidar
                    </p>
                  </template>
                </Row>
                <Row v-if="!hideDetailsCostsAndNetBalance(investment.subtype)">
                  <template #top-right>
                    <p class="text-p-3 position-investment__details--title">
                      {{ investment.netValue | money }}
                    </p>
                  </template>
                  <template #top-left>
                    <p class="text-p-5 position-investment__details--subtitle">
                      Valor líquido
                    </p>
                  </template>
                </Row>
              </div>
            </div>
          </Accordion>
        </AccordionWrapper>
      </template>
    </div>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

import {
  ActionableHelperView,
  NavBar,
  Heading,
  Row,
  BaseText,
  ContextColorProvider,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  AccordionWrapper,
  Accordion
} from '@/modules/common/components/Accordion';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import {
  OverviewAPI,
  PositionDetail
} from '@/modules/common/services/http/overview';
import { FeatureFlag } from '@/modules/common/helpers/feature-flag';
import { EIntrumentQuotaSubtype } from '@/types/models/Portfolio';
import { StorageKeys } from '@/modules/common/services/local-storage';
import {
  trackPositionDetailBackClick,
  trackPositionDetailViewLoad
} from './track-events';

@Component({
  components: {
    ContextColorProvider,
    NavBar,
    BackButton,
    LoaderIndicator,
    Heading,
    Row,
    Accordion,
    AccordionWrapper,
    ActionableHelperView,
    BaseText
  },
  filters: {
    money: (number: number, maximumFractionDigits = 2) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits
      }).format(number);
    }
  }
})
export default class OverviewPositionDetail extends Vue {
  private NebraskaColors = NebraskaColors;
  private position?: PositionDetail = undefined;
  private productApiId?: string = undefined;
  private isLoading = true;
  private hasError = false;

  private featureFlag = new FeatureFlag();
  private shouldShowNetBalanceAndTax = this.featureFlag.isLocalEnabled(
    'ENABLE_NET_BALANCE_PENSION_EXTRACT_DETAIL_PENSION'
  );

  private async created() {
    await this.loadPosition();
    trackPositionDetailViewLoad();
  }

  private back() {
    this.$router.back();
    trackPositionDetailBackClick();
  }

  public get showContractedRate() {
    return (
      this.position?.investments[0].benchmarkRate ||
      this.position?.investments[0].fixedRate
    );
  }

  private get formatFixedTax() {
    const investment = this.position && this.position.investments[0];
    let parseFixedTax = '';

    if (!investment) return '';

    if (investment.fixedRate)
      parseFixedTax += `${investment.fixedRate.toFixed(2)}%`;

    if (investment.fixedRate && investment.benchmarkRate)
      parseFixedTax += ` + `;

    if (investment.benchmarkRate && investment.benchmarkCode) {
      parseFixedTax += `${investment.benchmarkRate}% do ${investment.benchmarkCode}`;
    }

    return parseFixedTax;
  }

  get hasAnsweredResearch() {
    const hasAnswered = !!localStorage.getItem(
      StorageKeys.HasUsedPositionDetailsButton
    );
    return hasAnswered;
  }

  private hideDetailsCostsAndNetBalance(subtype: number) {
    return (
      !this.shouldShowNetBalanceAndTax &&
      subtype === EIntrumentQuotaSubtype.PENSION
    );
  }

  private async loadPosition() {
    this.isLoading = true;
    this.productApiId = this.$route.params.productApiId;

    try {
      this.position = await OverviewAPI.getPosition(this.productApiId);
    } catch {
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<style lang="less" scoped>
.position-detail__back-button {
  width: 32px;
}

.position__helper-view {
  display: flex;
  align-items: center;
  .position__error {
    width: 100%;
  }
}

.position-detail__content {
  max-width: 1000px;
  margin: 0 auto;
  padding: @size-spacing-x800 @size-spacing-x500;
}

.position__subtitle {
  color: @text-secondary!important;
}

.position-investment__details {
  padding: 0 @size-spacing-x500;
  margin-bottom: @size-spacing-x600;
}

.position__secondary-subtitle {
  &:not(:empty) {
    margin: @size-spacing-x350 0;
  }

  & > p {
    color: @text-secondary!important;
  }
}

.product-without-quotas {
  &_title {
    margin-bottom: @size-spacing-x350;
  }

  &_divider {
    border-top: 1px solid @divider-primary;
  }

  &_text {
    margin-top: @size-spacing-x500;
  }
}
</style>
